import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { AgentMcpPutItem, McpServer, ToolPermissionLevel } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AgentDraft } from "@/features/agents/components/AgentDraft";
import { useLlmModels } from "@/features/llms/hooks/useLlmModels";
import { useMcps, useMcpTools } from "@/features/mcps/hooks/useMcps";
import { useSkills } from "@/features/skills/hooks/useSkills";
import { cn } from "@/lib/utils";

const LEVELS: ToolPermissionLevel[] = ["auto", "requires_approval", "blocked"];

interface Props {
  draft: AgentDraft;
  onChange: (next: AgentDraft) => void;
}

export function ToolsTab({ draft, onChange }: Props) {
  const { t } = useTranslation("agents");
  const mcpsQuery = useMcps();
  const mcps = mcpsQuery.data ?? [];
  const skillsQuery = useSkills();
  const skills = skillsQuery.data ?? [];
  const llmModelsQuery = useLlmModels({ include_inactive: true });
  const selectedLlm = useMemo(
    () =>
      (llmModelsQuery.data ?? []).find((m) => m.id === draft.llm_model_id) ??
      null,
    [llmModelsQuery.data, draft.llm_model_id],
  );
  const webSearchSupported = selectedLlm?.supports_web_search ?? false;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function isActive(mcpId: string): boolean {
    return draft.mcp_items.some((m) => m.mcp_server_id === mcpId);
  }

  function getItem(mcpId: string): AgentMcpPutItem | undefined {
    return draft.mcp_items.find((m) => m.mcp_server_id === mcpId);
  }

  function setItem(mcpId: string, next: AgentMcpPutItem | null) {
    const others = draft.mcp_items.filter((m) => m.mcp_server_id !== mcpId);
    onChange({
      ...draft,
      mcp_items: next ? [...others, next] : others,
    });
  }

  function toggleMcp(mcp: McpServer, on: boolean) {
    if (on) {
      setItem(mcp.id, {
        mcp_server_id: mcp.id,
        enabled_tools: mcp.enabled_tools ? [...mcp.enabled_tools] : null,
        tool_permissions: {},
      });
    } else {
      setItem(mcp.id, null);
    }
  }

  function toggleSkill(skillId: string, on: boolean) {
    const next = on
      ? [...new Set([...draft.skill_ids, skillId])]
      : draft.skill_ids.filter((id) => id !== skillId);
    onChange({ ...draft, skill_ids: next });
  }

  function toggleTool(mcpId: string, toolName: string, on: boolean) {
    const item = getItem(mcpId);
    if (!item) return;
    const current = item.enabled_tools ?? [];
    let next: string[];
    if (on) {
      next = [...new Set([...current, toolName])];
    } else {
      next = current.filter((t) => t !== toolName);
    }
    const newPerms = { ...item.tool_permissions };
    if (!on) delete newPerms[toolName];
    setItem(mcpId, { ...item, enabled_tools: next, tool_permissions: newPerms });
  }

  function setPermission(mcpId: string, toolName: string, level: ToolPermissionLevel) {
    const item = getItem(mcpId);
    if (!item) return;
    setItem(mcpId, {
      ...item,
      tool_permissions: {
        ...item.tool_permissions,
        [toolName]: { level },
      },
    });
  }

  function toggleExpand(mcpId: string) {
    const next = new Set(expanded);
    if (next.has(mcpId)) next.delete(mcpId);
    else next.add(mcpId);
    setExpanded(next);
  }

  return (
    <div className="space-y-6">
      {/* Seção 1: Ferramentas built-in (sempre visível). */}
      <section className="space-y-3">
        <header>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t("tools.builtin.title")}
          </h3>
        </header>

        {/* Acesso à web. */}
        <div
          className={cn(
            "space-y-2 rounded-md border p-4",
            !webSearchSupported && "opacity-80",
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <Label
                htmlFor="agent-web-search-enabled"
                className="flex items-center gap-2 font-medium"
              >
                <input
                  id="agent-web-search-enabled"
                  type="checkbox"
                  checked={draft.web_search_enabled && webSearchSupported}
                  disabled={!webSearchSupported}
                  onChange={(e) =>
                    onChange({
                      ...draft,
                      web_search_enabled: e.target.checked,
                    })
                  }
                />
                {t("tools.webSearch.title")}
              </Label>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("tools.webSearch.description")}
              </p>
              {!webSearchSupported && selectedLlm ? (
                <p
                  className="mt-2 text-xs text-amber-700"
                  title={t("tools.webSearch.unsupportedTooltip")}
                >
                  {t("tools.webSearch.unsupportedBadge", {
                    name: selectedLlm.display_name,
                  })}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        {/* Geração de relatórios. */}
        <div className="space-y-2 rounded-md border p-4">
          <Label
            htmlFor="agent-reports-enabled"
            className="flex items-center gap-2 font-medium"
          >
            <input
              id="agent-reports-enabled"
              type="checkbox"
              checked={draft.reports_enabled}
              onChange={(e) =>
                onChange({ ...draft, reports_enabled: e.target.checked })
              }
            />
            {t("tools.reports.title")}
          </Label>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("tools.reports.description")}
          </p>
        </div>
      </section>

      {/* Seção 2: Servidores MCP. */}
      <section className="space-y-3">
        <header>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t("tools.mcps.title")}
          </h3>
        </header>
        {mcps.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("tools.empty")}</p>
        ) : (
          mcps.map((mcp) => (
            <McpCard
              key={mcp.id}
              mcp={mcp}
              isActive={isActive(mcp.id)}
              isExpanded={expanded.has(mcp.id)}
              item={getItem(mcp.id)}
              onToggleActive={(on) => toggleMcp(mcp, on)}
              onToggleExpand={() => toggleExpand(mcp.id)}
              onToggleTool={(name, on) => toggleTool(mcp.id, name, on)}
              onSetPermission={(name, level) =>
                setPermission(mcp.id, name, level)
              }
            />
          ))
        )}
      </section>

      {/* Seção 3: Skills (liga/desliga, sem sub-config). */}
      <section className="space-y-3">
        <header>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t("tools.skills.title")}
          </h3>
        </header>
        {skills.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("tools.skills.empty")}
          </p>
        ) : (
          skills.map((skill) => {
            const checked = draft.skill_ids.includes(skill.id);
            return (
              <div key={skill.id} className="rounded-md border p-3">
                <Label
                  htmlFor={`skill-${skill.id}`}
                  className="flex cursor-pointer items-start gap-3"
                >
                  <input
                    id={`skill-${skill.id}`}
                    type="checkbox"
                    className="mt-1"
                    checked={checked}
                    onChange={(e) => toggleSkill(skill.id, e.target.checked)}
                  />
                  <span className="flex-1">
                    <span className="font-medium">{skill.name}</span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {skill.description}
                    </span>
                  </span>
                </Label>
              </div>
            );
          })
        )}
      </section>
    </div>
  );
}

interface CardProps {
  mcp: McpServer;
  isActive: boolean;
  isExpanded: boolean;
  item: AgentMcpPutItem | undefined;
  onToggleActive: (on: boolean) => void;
  onToggleExpand: () => void;
  onToggleTool: (toolName: string, on: boolean) => void;
  onSetPermission: (toolName: string, level: ToolPermissionLevel) => void;
}

function McpCard({
  mcp,
  isActive,
  isExpanded,
  item,
  onToggleActive,
  onToggleExpand,
  onToggleTool,
  onSetPermission,
}: CardProps) {
  const { t } = useTranslation("agents");
  // Carrega as tools disponiveis do MCP (cacheado 60s via Redis no backend).
  const toolsQuery = useMcpTools(isActive ? mcp.id : "");
  const liveTools = toolsQuery.data?.tools ?? [];
  // Whitelist do MCP (config admin). Se null = sem whitelist (qualquer
  // tool descoberta vale).
  const allowed = mcp.enabled_tools;

  /** Tools que o agent pode listar como "enabled":
   * - se `mcp.enabled_tools` esta definido, intersecta com tools descobertas.
   * - se null, todas as tools descobertas valem.
   */
  const candidates = liveTools
    .filter((t) => (allowed ? allowed.includes(t.name) : true))
    .map((t) => t.name);

  const [filterText, setFilterText] = useState("");
  const filteredCandidates = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((name) => name.toLowerCase().includes(q));
  }, [candidates, filterText]);

  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center gap-3">
        <input
          id={`mcp-${mcp.id}`}
          type="checkbox"
          checked={isActive}
          onChange={(e) => onToggleActive(e.target.checked)}
        />
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex flex-1 items-center justify-between text-left"
        >
          <Label
            htmlFor={`mcp-${mcp.id}`}
            className="flex cursor-pointer items-center gap-2"
          >
            <span className="font-medium">{mcp.name}</span>
            {!mcp.is_active ? (
              <Badge variant="destructive" className="text-xs">
                {t("tools.inactive")}
              </Badge>
            ) : null}
          </Label>
          {isActive ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )
          ) : null}
        </button>
      </div>

      {isActive && isExpanded ? (
        <div className="mt-3 space-y-3 border-t pt-3">
          {candidates.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {toolsQuery.isLoading
                ? t("tools.fetching")
                : t("tools.noToolAvailable")}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[160px]">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-400" />
                  <Input
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    placeholder={t("tools.filter.placeholder")}
                    className="pl-8 text-xs"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    for (const name of filteredCandidates) {
                      onToggleTool(name, true);
                    }
                  }}
                  disabled={filteredCandidates.length === 0}
                >
                  {t("tools.actions.selectAllVisible")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    for (const name of filteredCandidates) {
                      onToggleTool(name, false);
                    }
                  }}
                  disabled={filteredCandidates.length === 0}
                >
                  {t("tools.actions.deselectAllVisible")}
                </Button>
              </div>
              {filteredCandidates.length === 0 ? (
                <p className="rounded-md border border-dashed py-3 text-center text-xs text-muted-foreground">
                  {t("tools.filter.noResults")}
                </p>
              ) : (
                <ul className="space-y-2">
                  {filteredCandidates.map((toolName) => {
                    const enabled =
                      item?.enabled_tools?.includes(toolName) ?? false;
                    const level: ToolPermissionLevel =
                      item?.tool_permissions[toolName]?.level ?? "auto";
                    return (
                      <li
                        key={toolName}
                        className="flex items-center gap-3 rounded-md border bg-muted/30 p-2"
                      >
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) =>
                            onToggleTool(toolName, e.target.checked)
                          }
                        />
                        <span className="flex-1 font-mono text-xs">
                          {toolName}
                        </span>
                        {enabled ? (
                          <Select
                            value={level}
                            onValueChange={(v) =>
                              onSetPermission(
                                toolName,
                                v as ToolPermissionLevel,
                              )
                            }
                          >
                            <SelectTrigger className="h-8 w-[180px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {LEVELS.map((l) => (
                                <SelectItem key={l} value={l}>
                                  {t(`tools.permissions.${l}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
